// Single source of truth for the TrainForge legal text (Terms of Service,
// Privacy Policy, Fitness Liability Waiver, AI + allergy disclaimers).
//
// Consumed by:
//  - RequireTerms.jsx  -> renders the gate + snapshots the plain text at the
//    moment of acceptance onto the member/staff doc (acceptedTermsText), so the
//    record always reflects exactly what was agreed even after future updates.
//  - LegalDocument.jsx -> renders these sections (gate + Profile expand).
//  - Profile LegalSection -> shows/downloads the snapshot, falling back to the
//    current text for users who accepted before snapshots were stored.
//
// When the text changes materially, bump CURRENT_TERMS_VERSION (and
// TERMS_LAST_UPDATED). RequireTerms re-prompts everyone below the new version.

export const CURRENT_TERMS_VERSION = 2;
export const TERMS_LAST_UPDATED = 'May 2026';

// Each section: a heading + paragraphs. A paragraph may have a bold `lead`
// (e.g. "ASSUMPTION OF RISK:") rendered inline before its text.
export const LEGAL_SECTIONS = [
  {
    heading: 'Terms of Service',
    paragraphs: [
      { text: 'By using this application ("TrainForge"), you agree to these terms. TrainForge is provided by the gym and its technology partner. You must be 16 or older to use the service. You are responsible for maintaining the security of your account credentials.' },
      { text: 'We reserve the right to suspend or terminate accounts that violate these terms or engage in abusive behavior toward other members, trainers, or staff.' },
    ],
  },
  {
    heading: 'Privacy Policy',
    paragraphs: [
      { lead: 'Data we collect:', text: 'Name, email, body measurements (weight, body-fat %, skeletal muscle), workout history (exercises, sets, reps, weight), profile information (age, height, goals, injuries, food allergies, diet type), booking records, food photos and nutrition logs.' },
      { lead: 'How we use it:', text: 'To provide personalized training programs, nutrition tracking, and macro recommendations. AI program and nutrition analysis sends your profile data and food photos to OpenAI for processing. No data is retained by OpenAI beyond the request. Food photos may be stored for your personal log review.' },
      { lead: 'Who has access:', text: "Your gym's owners and assigned trainers can view your workout history, measurements, and nutrition logs to provide coaching. Other members cannot see your data. Your data is never sold to third parties." },
      { lead: 'Data storage:', text: 'Stored securely on Google Cloud (Firebase) with encryption at rest and in transit. Servers located in the United States (Columbus, OH region).' },
      { lead: 'Your rights:', text: 'You may request a full export of your data or deletion of your account at any time by contacting the gym owner, your platform administrator, or emailing support. We will process deletion requests within 30 days.' },
    ],
  },
  {
    heading: 'Fitness Liability Waiver',
    paragraphs: [
      { lead: 'ASSUMPTION OF RISK:', text: 'I understand that physical exercise involves inherent risks of injury. I voluntarily assume full responsibility for any risks of loss, property damage, or personal injury that may be sustained as a result of participating in any exercise program, whether suggested by this app, a trainer, or an AI system.' },
      { lead: 'RELEASE OF LIABILITY:', text: 'I release and hold harmless the fitness facility, TrainForge, its owners, trainers, technology partners, and affiliates from any and all liability for injuries, damages, or losses arising from my use of this application or participation in any exercise program.' },
      { lead: 'MEDICAL CLEARANCE:', text: 'I confirm that I have consulted with a qualified physician and have been cleared for physical activity, or I accept full responsibility for exercising without such clearance. I will cease activity and seek medical attention if I experience pain, dizziness, or other concerning symptoms.' },
    ],
  },
  {
    heading: 'AI-Generated Content Disclaimer',
    paragraphs: [
      { text: 'Workout programs, daily check-ins, macro suggestions, and nutrition estimates generated by AI are algorithmic suggestions based on the information you provide. They are not professional medical, fitness, or nutritional advice. Calorie and macro estimates from food photos are approximate (plus or minus 25% accuracy) and should not be used for medical dietary management. Always consult qualified professionals for medical conditions, injuries, allergies, or dietary needs. TrainForge and its gym partners disclaim all liability for outcomes resulting from AI-generated recommendations.' },
    ],
  },
  {
    heading: 'Food Allergy Disclaimer',
    paragraphs: [
      { text: 'While you may list food allergies in your profile, AI-generated meal suggestions and food analysis are not guaranteed to detect or avoid allergens. Always verify ingredients independently if you have severe food allergies. TrainForge is not responsible for allergic reactions resulting from AI food recommendations.' },
    ],
  },
];

/**
 * Serialize the legal text to a plain-text document. This is what gets
 * snapshotted at acceptance and what the Download button produces.
 *
 * @param {Object} [meta] - optional acceptance metadata to stamp in a header
 *   { version, acceptedAt (Date|string), email }
 */
export function buildLegalTextPlain(meta = {}) {
  const lines = [];
  lines.push('TrainForge Terms of Service, Privacy Policy & Fitness Liability Waiver');
  lines.push(`Version ${meta.version ?? CURRENT_TERMS_VERSION} . Last updated ${TERMS_LAST_UPDATED}`);
  if (meta.acceptedAt) lines.push(`Accepted: ${meta.acceptedAt}`);
  if (meta.email) lines.push(`By: ${meta.email}`);
  lines.push('');
  for (const section of LEGAL_SECTIONS) {
    lines.push(section.heading.toUpperCase());
    for (const p of section.paragraphs) {
      lines.push(p.lead ? `${p.lead} ${p.text}` : p.text);
    }
    lines.push('');
  }
  return lines.join('\n').trimEnd() + '\n';
}
